import React, { useState, useEffect } from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import { showToast } from '@deeptrue-frontend/ui';
// Commented stuff will be useful later

// import env from 'consts/env';
// import { ReactComponent as Logo } from 'assets/images/logo.svg';
import HubSpot from 'components/HubSpot/HubSpot';
import ForAdminOnly from 'components/ForAdminOnly/ForAdminOnly';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import LogoutButton from './LogoutButton';
import { useFormatMessage } from 'components/IntlProvider';
// import UserName from './UserName';
import { ReactComponent as SettingsOutlinedIcon } from '../../../assets/icons/settings.svg';
import { authSelectors } from 'store/modules/auth/auth';
import {
  MainMenu,
  MenuListItem,
  TopNavbar,
  MainContainer,
  Notification,
  Spinner,
} from '@deeptrue-frontend/ui';
import { ReactComponent as DashboardIcon } from '../../../assets/icons/dashboard/dashboard-default.svg';
// import { ReactComponent as NotificationIcon } from '../../../assets/icons/notification/notification-default.svg';
import { ReactComponent as InnovationIcon } from '../../../assets/icons/innovation/innovation-default.svg';
import { ReactComponent as PaymentsIcon } from '../../../assets/icons/token.svg';
import { ReactComponent as SupportIcon } from '../../../assets/icons/support.svg';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { UserType } from 'store/modules/auth/auth';
import env from 'consts/env';
import api from 'api';
import { LanguageMenu } from './Menu';

const { Link, ROUTES } = RouterProvider;
const { connect, actions, selectors } = StoreProvider;
const ONE_SECOND = 1000;
const SECONDS_THRESHOLD = 30;

interface Props {
  isOpen: boolean;
}

const Anchor = styled.a`
  color: ${({ theme }) => theme.colors.colorPrimary};
  margin-left: 0.5rem;
  cursor: pointer;
`;
const SpinnerStyled = styled(Spinner)`
  margin-left: 0.5rem;
`;

const StyledMainContainerWrapper = styled.div<Props>`
  width: calc(100% - 2rem);
  padding-top: 4rem;
  padding-left: ${(props) => (props.isOpen ? '18.75rem' : '4rem')};
  min-height: 100vh;
  flex-direction: column;
  flex-grow: 1;
  transition: padding-left 0.3s;
  background: #fafafa;
  margin-left: 2rem;

  &.is-notification-active {
    padding-top: 7rem;
  }
`;

// eslint-disable-next-line
const StyledLink = styled(({ isActive, ...rest }) => <Link {...rest} />)`
  cursor: pointer;
  color: inherit;
  text-decoration: inherit;
  margin-right: 2rem;
  border-radius: 0 3rem 3rem 0;

  &:hover {
    box-shadow: inset 4px 0px ${({ theme }) => theme.colors.royalBlue};
    background: #ccebff;
  }
  &.active {
    box-shadow: inset 4px 0px ${({ theme }) => theme.colors.royalBlue};
    background: #ccebff;
  }

  ${({ isActive, theme }) =>
    isActive &&
    `
  box-shadow: inset 4px 0px  ${theme.colors.royalBlue};
  background: #ccebff;
  `}
`;

const StyledAnchor = styled.a`
  cursor: pointer;
  color: inherit;
  text-decoration: inherit;
  margin-right: 2rem;
  border-radius: 0 3rem 3rem 0;

  &:hover {
    box-shadow: inset 4px 0px ${({ theme }) => theme.colors.royalBlue};
    background: #ccebff;
  }
  &.active {
    box-shadow: inset 4px 0px ${({ theme }) => theme.colors.royalBlue};
    background: #ccebff;
  }
`;

const StyledContainer = styled((props) => <Grid component="div" {...props} />)`
  width: 100%;
  margin: 0;
  margin-top: 0 !important;
`;

const StyledMainContainer = styled(MainContainer)<{ isFullWidth?: boolean }>`
  ${(props) =>
    props.isFullWidth &&
    `
    max-width: 100%;
  `}
  width: calc(100% - 2rem);
  padding: 0;
  .survey-instance {
    min-height: calc(100vh - 182px);
  }
  margin-left: 0;
`;

interface DashboardProps {
  children: React.ReactNode;
  location: RouteComponentProps['location'];
  title?: string;
  combinedTitle?: {
    projectName: string;
    toolName: string;
    status: { value: string; text: string };
  };
  displayName?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  headerChildren?: React.ReactNode;
  isMenuOpen: boolean;
  toggleMenu: (isMenuOpen: boolean) => void;
  user: UserType;
  userUpdate: (user: UserType) => void;
  isFullWidth?: boolean;
  isBexioUser: boolean;
  isOrganizationAdmin: boolean;
}

export const Dashboard = ({
  children,
  title,
  combinedTitle,
  displayName,
  headerTitle,
  headerSubtitle,
  isMenuOpen,
  toggleMenu,
  headerChildren,
  user,
  userUpdate,
  isFullWidth,
  isBexioUser,
  isOrganizationAdmin,
}: DashboardProps) => {
  const [isOpen, setOpen] = useState(isMenuOpen);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const t = useFormatMessage();
  const { is_password_set, email } = user;
  const isNotificationActive = !is_password_set;
  const location = useLocation();

  const resendEmail = () => {
    setIsResendingEmail(true);
    api.resources
      .forgotPasswordSendEmail({ email: email })
      .then(() => {
        setIsResendingEmail(false);
      })
      .catch(() => {
        setIsResendingEmail(false);
        showToast({
          type: 'error',
          title: t(
            'password.reset.failed',
            'You provided not valid email address'
          ),
        });
      });
  };

  async function getUser(interval?: any) {
    const { data } = await api.resources.getUser(user);
    const { is_password_set } = data;
    if (is_password_set) {
      if (interval) {
        clearInterval(interval);
      }
      userUpdate(data);
    }
  }

  const handleChange = () => {
    toggleMenu(!isOpen);
    setOpen(!isOpen);
  };
  useEffect(() => {
    if (user.uuid && isNotificationActive) {
      getUser();
      const interval: any = setInterval(
        () => getUser(interval),
        SECONDS_THRESHOLD * ONE_SECOND
      );
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line
  }, []);

  const isProjectRoute = location && location.pathname.includes('/project/');
  // NOTE Bexio user do not have Overview tab as this ones redirect to project details
  const shouldDisplayRouteActive = isProjectRoute && isBexioUser;
  const isOrganizationUserManagmentRoute =
    location && location.pathname.includes('/organization/');
  return (
    <>
      <HubSpot />
      <MainMenu
        isOpen={isOpen}
        onChange={handleChange}
        logoUrl={env.LANDINGPAGE_URL}
      >
        <StyledLink
          exact
          to={ROUTES.DASHBOARD}
          isActive={shouldDisplayRouteActive}
        >
          <MenuListItem title={t('dashboard.menu.overview', 'Overview')}>
            <DashboardIcon />
          </MenuListItem>
        </StyledLink>
        {/* TODO add translations and access to ROUTE */}
        {isOrganizationAdmin && (
          <StyledLink
            exact
            to={ROUTES.ORGANIZATION.ADMIN.USERS}
            isActive={isOrganizationUserManagmentRoute}
          >
            <MenuListItem title={t('dashboard.menu.admin', 'Admin')}>
              <SettingsOutlinedIcon />
            </MenuListItem>
          </StyledLink>
        )}
        {/* ToDo: Add logic for Notifications */}
        {/* <StyledLink exact to={ROUTES.NOTIFICATIONS}>
          <MenuListItem title={'Notifications'}>
            <NotificationIcon />
          </MenuListItem>
        </StyledLink> */}
        <ForAdminOnly>
          <StyledLink exact to={ROUTES.ADMIN.ORGANIZATIONS.LIST}>
            <MenuListItem
              title={t('dashboard.menu.deeptrueAdmin', 'Deeptrue Admin')}
            >
              <InnovationIcon />
            </MenuListItem>
          </StyledLink>
        </ForAdminOnly>
        {!isBexioUser && (
          <StyledLink exact to={ROUTES.PAYMENTS.MAIN}>
            <MenuListItem title={t('dashboard.menu.payments', 'Payments')}>
              <PaymentsIcon />
            </MenuListItem>
          </StyledLink>
        )}
        <StyledLink to={ROUTES.SETTINGS.PROFILE}>
          <MenuListItem isUserBlock>
            <p>{displayName}</p>
          </MenuListItem>
        </StyledLink>
        <StyledAnchor
          href="https://deeptrue.atlassian.net/servicedesk/customer/portal/1"
          target="_blank"
          rel="noopener noreferrer"
        >
          <MenuListItem title={t('dashboard.menu.serviceDesk', 'Service Desk')}>
            <SupportIcon />
          </MenuListItem>
        </StyledAnchor>
        <LanguageMenu isMenuOpen={isOpen} />
        <LogoutButton />
      </MainMenu>
      {isNotificationActive && (
        <Notification
          marginLeft={isOpen ? '300px' : '64px'}
          text={
            <span>
              {t(
                'password.resendLink.text',
                'Check your emails for setting up your password.'
              )}
              {isResendingEmail ? (
                <SpinnerStyled size="14px" color="#1F5DEA" />
              ) : (
                <Anchor onClick={resendEmail}>
                  {t('password.resendLink.cta', 'Resend the email')}
                </Anchor>
              )}
            </span>
          }
        />
      )}
      <TopNavbar
        isNotificationActive={isNotificationActive}
        title={title ? title : t('dashboard.menu.dashboard', 'Dashboard')}
        combinedTitle={combinedTitle}
        marginLeft={isOpen ? '300px' : '64px'}
        headerChildren={headerChildren}
        isFullWidth={isFullWidth}
      />

      <StyledMainContainerWrapper
        isOpen={isOpen}
        className={isNotificationActive ? 'is-notification-active' : ''}
      >
        <StyledMainContainer
          title={headerTitle}
          subtitle={headerSubtitle}
          isFullWidth={isFullWidth}
        >
          <StyledContainer container>{children}</StyledContainer>
        </StyledMainContainer>
      </StyledMainContainerWrapper>
    </>
  );
};

export default connect(
  (state) => ({
    displayName: selectors.authSelectors.userDisplayName(state),
    isMenuOpen: selectors.ui.isMenuOpen(state),
    user: selectors.authSelectors.userProfile(state),
    isBexioUser: selectors.authSelectors.isBexioUser(state),
    isOrganizationAdmin: authSelectors.isOrganizationAdmin(state),
  }),
  {
    toggleMenu: actions.ui.setToggleMenu,
    userUpdate: actions.authActions.userUpdate,
  }
)(Dashboard);
